import React from 'react';
import { cn } from '@/lib/utils';
import { useFieldLocked } from './FieldLock';

/** Inputs whose click opens a picker, which `readOnly` does not stop. */
// Locked checkboxes, radios and ranges rely on being controlled: dropping
// onChange makes React put them back. preventDefault on their click is NOT
// used because it fights React's controlled restore. Keep them controlled.
const PICKER_TYPES = new Set(['file', 'color']);

/**
 * What an input inside a locked FieldLockScope gets on top of its props: it
 * keeps its value, takes no change (its onChange is dropped, so React also puts
 * a controlled checkbox or radio straight back), and is never natively
 * `disabled` (that greys it, which a locked field must not look like).
 */
function lockedInputProps(type: string | undefined): React.InputHTMLAttributes<HTMLInputElement> {
  return {
    disabled: undefined,
    readOnly: true,
    onChange: undefined,
    ...(type && PICKER_TYPES.has(type) ? { onClick: (e) => e.preventDefault() } : {}),
  };
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  /** Required indicator (*) next to label */
  required?: boolean;
  /** Icon/element at the left edge inside the input */
  leftAddon?: React.ReactNode;
  /** Icon/element at the right edge inside the input */
  rightAddon?: React.ReactNode;
  /** Full-width by default */
  fullWidth?: boolean;
  wrapperClassName?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      required,
      leftAddon,
      rightAddon,
      fullWidth = true,
      wrapperClassName,
      className,
      id,
      ...props
    },
    ref,
  ) => {
    const locked = useFieldLocked();
    const inputId = id ?? (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    const errorId = error ? `${inputId}-error` : undefined;
    const hintId = hint ? `${inputId}-hint` : undefined;

    /**
     * A native date control ignores `placeholder` outright, so the only way to show
     * one is to paint it over the empty field. Below `md` that matters: an empty
     * date input reads as a blank box beside text fields that all show their hint.
     */
    const datePlaceholderShown = props.type === 'date' && !props.value && !!props.placeholder;

    return (
      <div className={cn(fullWidth && 'w-full', wrapperClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-slate-700 mb-1.5"
          >
            {label}
            {required && (
              <span className="ml-0.5 text-red-500" aria-hidden="true">
                {' *'}
              </span>
            )}
          </label>
        )}

        <div className="relative">
          {leftAddon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-slate-400 text-sm">{leftAddon}</span>
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            required={required}
            aria-invalid={!!error}
            aria-describedby={[errorId, hintId].filter(Boolean).join(' ') || undefined}
            data-placeholder-shown={datePlaceholderShown ? 'true' : undefined}
            className={cn(
              'form-input-base',
              leftAddon && 'pl-9',
              rightAddon && 'pr-9',
              error && 'form-input-error',
              // Marker only — `peer` emits no CSS of its own. It lets the overlay
              // below react to focus without this component tracking focus state.
              datePlaceholderShown && 'peer',
              className,
            )}
            {...props}
            {...(locked ? lockedInputProps(props.type) : {})}
          />

          {/*
            The placeholder overlay. Absolutely positioned so it costs no layout, and
            `hidden` at the base with `.date-placeholder-overlay` on top, which is what
            keeps desktop exactly as it was — there it never generates a box and, being
            aria-hidden and display:none, never reaches the accessibility tree either.
            It sits after the input so the overlay's `:focus` sibling rule can reach it.
            The paired rule in index.css blanks the control's own text underneath.

            The utility replaced `max-md:flex max-md:peer-focus:hidden`: a Tailwind
            variant stops at 767.98px, so the overlay vanished on an iPad and on any
            phone in landscape, which is the "no placeholder text" half of SCRUM-1379.
          */}
          {datePlaceholderShown && (
            <span
              aria-hidden="true"
              data-testid="date-placeholder"
              className={cn(
                'pointer-events-none absolute inset-y-0 left-0 hidden items-center text-sm text-slate-400',
                'date-placeholder-overlay',
                leftAddon ? 'pl-9' : 'pl-3',
              )}
            >
              {props.placeholder}
            </span>
          )}

          {rightAddon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-slate-400 text-sm">{rightAddon}</span>
            </div>
          )}
        </div>

        {hint && !error && (
          <p id={hintId} className="mt-1.5 text-xs text-slate-500">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
